/**
 * Analytics Page
 * Dashboard with charts and metrics
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { apiClient } from '../lib/api';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Icon, type IconName } from '../components/icons';
import type { Post, Analytics as AnalyticsType } from '../types';

export function Analytics() {
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [analytics, setAnalytics] = useState<Record<string, AnalyticsType[]>>({});
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (selectedBrand) {
      fetchData();
    }
  }, [selectedBrand, timeRange]);

  const fetchData = async () => {
    if (!selectedBrand) return;

    try {
      const response = await apiClient.getPosts({
        brand_id: selectedBrand.id,
        status: 'published',
        limit: 100,
      });

      if (response.success && response.data) {
        setPosts(response.data);
        // Fetch analytics for a subset of recent posts to keep requests reasonable
        const published = response.data.filter((p: Post) => p.status === 'published').slice(0, 10);
        const analyticsEntries: Record<string, AnalyticsType[]> = {};
        await Promise.all(
          published.map(async (post) => {
            try {
              const res = await apiClient.getPostAnalytics(post.id);
              if (res.success && res.data) {
                analyticsEntries[post.id] = res.data as AnalyticsType[];
              }
            } catch (err) {
              console.error('Failed to fetch analytics for post', post.id, err);
            }
          })
        );
        setAnalytics(analyticsEntries);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const published = posts.filter((p) => p.status === 'published').slice(0, 10);
      await Promise.all(
        published.map(async (post) => {
          try {
            await apiClient.syncPostAnalytics(post.id);
          } catch (err) {
            console.error('Failed to sync analytics for post', post.id, err);
          }
        })
      );
      await fetchData();
    } finally {
      setIsSyncing(false);
    }
  };

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center">
          <p className="text-gray-500">Please select a brand from the dashboard</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // Calculate platform distribution from real posts data
  const platformData = [
    { name: 'Instagram', posts: posts.filter(p => p.platforms.includes('instagram')).length },
    { name: 'Facebook', posts: posts.filter(p => p.platforms.includes('facebook')).length },
    { name: 'Twitter', posts: posts.filter(p => p.platforms.includes('twitter')).length },
    { name: 'LinkedIn', posts: posts.filter(p => p.platforms.includes('linkedin')).length },
    { name: 'TikTok', posts: posts.filter(p => p.platforms.includes('tiktok')).length },
  ].filter(p => p.posts > 0);

  const analyticsEntries = Object.values(analytics).flat();
  const aggregated = analyticsEntries.reduce(
    (acc, cur) => {
      acc.likes += cur.likes || 0;
      acc.comments += cur.comments || 0;
      acc.shares += cur.shares || 0;
      acc.views += cur.views || 0;
      acc.reach += cur.reach || 0;
      return acc;
    },
    { likes: 0, comments: 0, shares: 0, views: 0, reach: 0 }
  );

  const platformPerformance = (() => {
    const byPlatform: Record<string, { platform: string; likes: number; comments: number; shares: number; views: number }> = {};
    analyticsEntries.forEach((entry) => {
      if (!byPlatform[entry.platform]) {
        byPlatform[entry.platform] = { platform: entry.platform, likes: 0, comments: 0, shares: 0, views: 0 };
      }
      byPlatform[entry.platform].likes += entry.likes || 0;
      byPlatform[entry.platform].comments += entry.comments || 0;
      byPlatform[entry.platform].shares += entry.shares || 0;
      byPlatform[entry.platform].views += entry.views || 0;
    });
    return Object.values(byPlatform);
  })();

  const COLORS = ['#3B82F6', '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B'];

  // Calculate stats from real posts data
  const totalPosts = posts.length;
  const scheduledPosts = posts.filter(p => p.status === 'scheduled').length;
  const publishedPosts = posts.filter(p => p.status === 'published').length;
  const draftPosts = posts.filter(p => p.status === 'draft').length;

  const stats = [
    {
      icon: 'trending-up' as IconName,
      label: 'Total Posts',
      value: totalPosts.toString(),
      change: `${platformData.length} platforms`,
      color: 'blue',
    },
    {
      icon: 'users' as IconName,
      label: 'Published',
      value: publishedPosts.toString(),
      change: 'posts live',
      color: 'purple',
    },
    {
      icon: 'heart' as IconName,
      label: 'Scheduled',
      value: scheduledPosts.toString(),
      change: 'upcoming',
      color: 'red',
    },
    {
      icon: 'message' as IconName,
      label: 'Drafts',
      value: draftPosts.toString(),
      change: 'in progress',
      color: 'green',
    },
    {
      icon: 'heart' as IconName,
      label: 'Engagements',
      value: (aggregated.likes + aggregated.comments + aggregated.shares).toLocaleString(),
      change: `${aggregated.views.toLocaleString()} views`,
      color: 'red',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Performance insights for{' '}
            <span
              className="font-medium"
              style={{ color: selectedBrand.color_primary }}
            >
              {selectedBrand.name}
            </span>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
          <div className="w-full sm:w-auto sm:min-w-[180px]">
            <Select
              options={[
                { value: '7d', label: 'Last 7 days' },
                { value: '30d', label: 'Last 30 days' },
                { value: '90d', label: 'Last 90 days' },
              ]}
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as typeof timeRange)}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSync}
            isLoading={isSyncing}
            disabled={posts.filter((p) => p.status === 'published').length === 0}
          >
            <Icon name="refresh" size="sm" className="mr-2" />
            Sync analytics
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-semibold text-gray-900 mt-2">
                  {stat.value}
                </p>
                <p className="text-sm text-green-600 mt-1">{stat.change}</p>
              </div>
              <div
                className={`p-3 rounded-lg bg-${stat.color}-100`}
              >
                <Icon name={stat.icon} size="lg" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Posts Activity Chart */}
      <Card>
        <CardHeader
          title="Posting Activity"
          subtitle="Posts created over time"
        />
        {posts.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={(() => {
              // Group posts by month
              const monthlyData: Record<string, { month: string; posts: number; published: number; scheduled: number }> = {};
              posts.forEach(post => {
                const date = new Date(post.created_at);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                if (!monthlyData[monthKey]) {
                  monthlyData[monthKey] = { month: monthLabel, posts: 0, published: 0, scheduled: 0 };
                }
                monthlyData[monthKey].posts++;
                if (post.status === 'published') monthlyData[monthKey].published++;
                if (post.status === 'scheduled') monthlyData[monthKey].scheduled++;
              });
              return Object.values(monthlyData).slice(-6);
            })()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="posts" name="Total Posts" fill="#3B82F6" />
              <Bar dataKey="published" name="Published" fill="#10B981" />
              <Bar dataKey="scheduled" name="Scheduled" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Icon name="trending-up" size="3xl" className="mx-auto mb-4 opacity-50" />
            <p>No posting activity yet</p>
            <p className="text-sm mt-1">Create posts to see activity trends</p>
          </div>
        )}
      </Card>

      {/* Platform Performance */}
      <Card>
        <CardHeader
          title="Platform Performance"
          subtitle="Likes, comments, shares, and views by platform (synced posts)"
        />
        {platformPerformance.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={platformPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="platform" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="likes" stackId="a" fill="#3B82F6" />
              <Bar dataKey="comments" stackId="a" fill="#8B5CF6" />
              <Bar dataKey="shares" stackId="a" fill="#F59E0B" />
              <Bar dataKey="views" stackId="b" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No analytics data yet. Click "Sync analytics" to pull fresh metrics.
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Distribution */}
        <Card>
          <CardHeader
            title="Posts by Platform"
            subtitle="Distribution of published posts"
          />
          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(e: any) => e.name}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="posts"
                >
                  {platformData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No published posts yet
            </div>
          )}
        </Card>

        {/* Performance by Platform */}
        <Card>
          <CardHeader
            title="Performance by Platform"
            subtitle="Average engagement per platform"
          />
          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={platformData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="posts" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No data available
            </div>
          )}
        </Card>
      </div>

      {/* Top Performing Posts */}
      <Card>
        <CardHeader
          title="Top Performing Posts"
          subtitle="Your most engaging content"
        />
        {posts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No published posts yet
          </div>
        ) : (
          <div className="space-y-4">
            {posts.slice(0, 5).map((post) => (
              <div
                key={post.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/posts/${post.id}`)}
              >
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {post.title || 'Untitled Post'}
                  </h4>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                    {post.content}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="capitalize">{post.platforms.join(', ')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
